import { ArrowUpRight, Check } from "lucide-react";
import { SITE } from "@/config/site";
import AnchorMark from "@/components/AnchorMark";

// 28-day program pricing tiers — used on /program
export function ProgramPricing() {
  const text = SITE.pricing.programText;
  const audio = SITE.pricing.programAudio;

  const Tier = ({ tier, price, label, link, included, badge, featured, testId }) => (
    <div
      data-testid={testId}
      className={`relative p-8 md:p-10 ${
        featured
          ? "bg-[#15243D] text-[#F7F5F0]"
          : "bg-[#F7F5F0] text-[#15243D] border border-[#15243D]/12"
      }`}
    >
      {badge && (
        <span
          className={`absolute top-0 right-0 px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase ${
            featured ? "bg-[#C07865] text-white" : "bg-[#15243D] text-[#F7F5F0]"
          }`}
        >
          {badge}
        </span>
      )}
      <p
        className={`text-xs tracking-[0.22em] uppercase mb-6 ${
          featured ? "text-[#C07865]" : "text-[#C07865]"
        }`}
      >
        {tier}
      </p>
      <div className="flex items-baseline gap-2 mb-2">
        <span
          className={`serif text-5xl md:text-6xl ${featured ? "text-[#F7F5F0]" : "text-[#15243D]"}`}
          style={{ fontWeight: 300 }}
        >
          ${price}
        </span>
        <span className={`text-sm ${featured ? "text-[#F7F5F0]/60" : "text-[#8A8D93]"}`}>
          one-time
        </span>
      </div>
      <h3
        className={`serif text-2xl md:text-3xl mb-8 leading-snug ${
          featured ? "text-[#F7F5F0]" : "text-[#15243D]"
        }`}
        style={{ fontWeight: 300 }}
      >
        {label}
      </h3>
      <ul className="space-y-3 mb-10">
        {included.map((it) => (
          <li
            key={it}
            className={`flex items-baseline gap-3 text-base font-light leading-relaxed ${
              featured ? "text-[#F7F5F0]/85" : "text-[#5A5D63]"
            }`}
          >
            <Check
              size={16}
              strokeWidth={1.5}
              className={featured ? "text-[#C07865]" : "text-[#C07865]"}
            />
            {it}
          </li>
        ))}
      </ul>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`${testId}-buy`}
        className={
          featured
            ? "inline-flex items-center gap-3 px-7 py-3.5 bg-[#C07865] text-white text-xs tracking-[0.14em] uppercase font-medium hover:bg-[#A66352] transition-colors"
            : "btn-primary"
        }
      >
        Begin the practice
        <ArrowUpRight size={16} strokeWidth={1.5} />
      </a>
    </div>
  );

  return (
    <section
      id="pricing"
      data-testid="program-pricing"
      className="max-w-7xl mx-auto px-6 md:px-12 mb-24 md:mb-32"
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12 md:mb-16">
        <div className="md:col-span-5">
          <p className="overline mb-6">Begin</p>
          <h2
            className="serif text-4xl md:text-5xl text-[#15243D] leading-[1.1]"
            style={{ fontWeight: 300 }}
          >
            Two ways<br />
            to <em className="italic text-[#C07865]">start</em>.
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7 md:pt-3">
          <p className="text-lg font-light leading-relaxed text-[#5A5D63]">
            The full 28-day program, delivered as a daily companion. One
            reflection in your inbox each morning — short enough to read with
            coffee, deep enough to carry through your day.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-px md:bg-[#15243D]/10">
        <Tier
          tier="Tier I — Text"
          price={text.price}
          label={text.label}
          link={text.link}
          testId="pricing-text"
          included={[
            "Daily email — one short practice for 28 mornings",
            "Lifetime access to the full program on this site",
            "Companion PDF for offline reading",
          ]}
        />
        <Tier
          tier="Tier II — Audio"
          price={audio.price}
          label={audio.label}
          link={audio.link}
          testId="pricing-audio"
          badge="Founding Member"
          featured
          included={[
            "Everything in the text edition",
            "Ben's audio reflection for each of the 28 days — released weekly",
            "Locked-in founding rate ($129 once all audio is live)",
            "Direct line to Ben for questions during the 28 days",
          ]}
        />
      </div>

      <p className="mt-6 text-xs text-[#8A8D93] text-center">
        Secure checkout via Stripe. One-time purchase. No subscription.
      </p>
    </section>
  );
}

// Coaching payment tiers — used on /agreement success
export function CoachingPayment() {
  const full = SITE.pricing.coachingFull;
  const split = SITE.pricing.coachingSplit;

  return (
    <div
      data-testid="coaching-payment"
      className="mt-12 mb-12 border border-[#15243D]/12"
    >
      <div className="p-8 md:p-10 border-b border-[#15243D]/10">
        <div className="flex items-baseline justify-between gap-4 mb-2">
          <p className="overline">Investment</p>
          <AnchorMark size={20} color="#C07865" />
        </div>
        <h3
          className="serif text-2xl md:text-3xl text-[#15243D] leading-snug"
          style={{ fontWeight: 300 }}
        >
          Choose how you’d like to begin.
        </h3>
        <p className="mt-3 text-base font-light text-[#5A5D63]">
          A 4–6 week 1:1 coaching container. Sessions, schedule, and structure
          are shaped together in your onboarding call.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#15243D]/10">
        <div className="p-8" data-testid="coaching-full">
          <p className="text-xs tracking-[0.18em] uppercase text-[#C07865] mb-3">
            Option I
          </p>
          <div className="flex items-baseline gap-2 mb-1">
            <span
              className="serif text-4xl text-[#15243D]"
              style={{ fontWeight: 300 }}
            >
              ${full.price.toLocaleString()}
            </span>
            <span className="text-sm text-[#8A8D93]">one-time</span>
          </div>
          <p
            className="serif text-lg text-[#15243D] mb-6"
            style={{ fontWeight: 300 }}
          >
            Full payment
          </p>
          <a
            href={full.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coaching-full-buy"
            className="btn-primary w-full justify-center"
          >
            Pay ${full.price.toLocaleString()}
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>

        <div className="p-8" data-testid="coaching-split">
          <p className="text-xs tracking-[0.18em] uppercase text-[#C07865] mb-3">
            Option II
          </p>
          <div className="flex items-baseline gap-2 mb-1">
            <span
              className="serif text-4xl text-[#15243D]"
              style={{ fontWeight: 300 }}
            >
              ${split.price.toLocaleString()}
            </span>
            <span className="text-sm text-[#8A8D93]">× 2 monthly</span>
          </div>
          <p
            className="serif text-lg text-[#15243D] mb-6"
            style={{ fontWeight: 300 }}
          >
            Two payments
          </p>
          <a
            href={split.link}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="coaching-split-buy"
            className="btn-secondary w-full justify-center"
          >
            Start ${split.price.toLocaleString()} / month
            <ArrowUpRight size={16} strokeWidth={1.5} />
          </a>
        </div>
      </div>

      <p className="px-8 py-5 text-xs text-[#8A8D93] border-t border-[#15243D]/10">
        Secure checkout via Stripe. Prefer Venmo or Zelle? Just reply to the
        confirmation email and we’ll arrange it.
      </p>
    </div>
  );
}
